// =======================================================
// PosterType — bold sans-serif words slammed onto the ribbon.
// Beat-synced word changes, optional horizontal tiling, slam-in motion.
// Designed for ultrawide LED ribbons (10900x540) but adapts to any size.
// =======================================================

import * as THREE from 'three';
import { Generator } from './Generator.js';

const DEFAULT_WORDS = 'ONE,TWO,THREE,FOUR,FIVE,SIX,SEVEN,EIGHT';

export class PosterType extends Generator {
  constructor() {
    super({
      name: 'Poster Type',
      category: 'intense',
      schema: {
        words:     { default: DEFAULT_WORDS, label: 'Words (,)' },
        accent:    { default: '#ff1f1f', type: 'color', label: 'Accent' },
        bg:        { default: '#000000', type: 'color', label: 'Background' },
        inverted:  { default: false, type: 'bool', label: 'Inverted (accent BG)' },
        weight: {
          default: '900', type: 'select', label: 'Weight',
          options: [['400','Regular'], ['700','Bold'], ['900','Black']],
        },
        family: {
          default: 'sans', type: 'select', label: 'Family',
          options: [
            ['sans',  'Helvetica/Arial'],
            ['cond',  'Impact (condensed)'],
            ['pumpa', 'Pumpa (project)'],
            ['mono',  'JetBrains Mono'],
          ],
        },
        repeat:    { default: 1, min: 1, max: 16, step: 1, label: 'Repeat across' },
        fillWidth: { default: 0.92, min: 0.2, max: 1.0, step: 0.01, label: 'Fill width' },
        fillHeight:{ default: 0.85, min: 0.1, max: 1.4, step: 0.01, label: 'Fill height' },
        stretchX:  { default: 1.0, min: 0.3, max: 4.0, step: 0.01, label: 'Stretch X' },
        slam:      { default: 0.10, min: 0.0, max: 0.6, step: 0.01, label: 'Slam ease' },
        jitter:    { default: 0.0,  min: 0, max: 0.05, step: 0.001, label: 'Jitter' },
        upper:     { default: true, type: 'bool', label: 'Uppercase' },
        wordsPerLoop: { default: 0, min: 0, max: 32, step: 1, label: 'Words/loop (0=beat)' },
      },
    });
    this._wordList = [];
    this._lastStepIndex = -1;
    this._currentWord = null;
  }

  onEnter(scene, engine) {
    this.engine = engine;

    this.off = document.createElement('canvas');
    this.off.width = engine.width;
    this.off.height = engine.height;
    this.offCtx = this.off.getContext('2d', { alpha: false });

    this.tex = new THREE.CanvasTexture(this.off);
    this.tex.minFilter = THREE.LinearFilter;
    this.tex.magFilter = THREE.LinearFilter;
    this.tex.generateMipmaps = false;

    const geo = new THREE.PlaneGeometry(2, 2);
    const mat = new THREE.MeshBasicMaterial({
      map: this.tex,
      transparent: false,
      depthWrite: false,
      depthTest: false,
    });
    this.mesh = new THREE.Mesh(geo, mat);
    this.mesh.frustumCulled = false;
    this._updateMeshScale();
    scene.add(this.mesh);
    this.track(this.mesh);
    this.track(this.tex);

    this._parseWords();
    this._redraw(this._wordAt(0));
  }

  onResize(w, h) {
    if (!this.off) return;
    this.off.width = w;
    this.off.height = h;
    this._updateMeshScale();
    this._redraw(this._currentWord || this._wordAt(0));
  }

  _updateMeshScale() {
    if (!this.mesh || !this.engine) return;
    const aspect = this.engine.width / this.engine.height;
    this.mesh.scale.set(aspect, 1, 1);
  }

  onParamChange(key) {
    if (key === 'words') {
      this._parseWords();
      this._lastStepIndex = -1;
      this._redraw(this._wordAt(0));
      return;
    }
    this._redraw(this._currentWord || this._wordAt(0));
  }

  _parseWords() {
    const raw = String(this.v('words') || '').split(',').map(s => s.trim()).filter(Boolean);
    this._wordList = raw.length ? raw : ['AI'];
  }

  _wordAt(stepIndex) {
    if (!this._wordList.length) return 'AI';
    const i = ((stepIndex % this._wordList.length) + this._wordList.length) % this._wordList.length;
    return this._wordList[i];
  }

  _fontFamily() {
    switch (this.v('family')) {
      case 'cond':  return `'Impact', 'Haettenschweiler', 'Oswald', 'Arial Narrow', sans-serif`;
      case 'pumpa': return `'Pumpa', 'Helvetica Neue', Helvetica, Arial, sans-serif`;
      case 'mono':  return `'JetBrains Mono', ui-monospace, Consolas, monospace`;
      case 'sans':
      default:      return `'Helvetica Neue', Helvetica, Arial, sans-serif`;
    }
  }

  _redraw(word) {
    if (!this.offCtx) return;
    this._currentWord = word;

    const ctx = this.offCtx;
    const w = this.off.width;
    const h = this.off.height;
    const inverted = this.v('inverted');
    const accent = this.v('accent');
    const bg = this.v('bg');
    const fillBg = inverted ? accent : bg;
    const fillFg = inverted ? bg : accent;

    const repeat = Math.max(1, Math.floor(this.v('repeat')));
    const fillWidth = Math.max(0.05, this.v('fillWidth'));
    const fillHeight = Math.max(0.05, this.v('fillHeight'));
    const stretchX = Math.max(0.05, this.v('stretchX'));
    const upper = this.v('upper');
    const text = upper ? String(word).toUpperCase() : String(word);

    ctx.fillStyle = fillBg;
    ctx.fillRect(0, 0, w, h);

    // Font size: target height fraction of canvas. Width is fitted per slot
    // by horizontal scaling (no shrink-to-fit on height).
    const fontPx = Math.max(8, Math.floor(h * fillHeight));
    ctx.fillStyle = fillFg;
    ctx.font = `${this.v('weight')} ${fontPx}px ${this._fontFamily()}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    const measured = Math.max(1, ctx.measureText(text).width);
    const slotW = (w * fillWidth) / repeat;
    const fitScaleX = (slotW / measured) * stretchX;

    const startX = (w - w * fillWidth) * 0.5;
    const cy = h * 0.5;

    for (let i = 0; i < repeat; i++) {
      const cx = startX + slotW * (i + 0.5);
      ctx.save();
      ctx.translate(cx, cy);
      ctx.scale(fitScaleX, 1);
      ctx.fillText(text, 0, 0);
      ctx.restore();
    }

    this.tex.needsUpdate = true;
  }

  update(dt, time, engine) {
    if (!this.mesh || !this.engine) return;
    const loop = engine.getLoopInfo(time);
    const wordsPerLoop = Math.floor(this.v('wordsPerLoop'));

    // Determine current step index + intra-step phase (0..1).
    let stepIndex, stepPhase;
    if (wordsPerLoop > 0) {
      const total = loop.phase * wordsPerLoop;
      stepIndex = Math.floor(total);
      stepPhase = total - stepIndex;
    } else {
      stepIndex = loop.beatIndex;
      stepPhase = loop.beatPhase;
    }

    if (stepIndex !== this._lastStepIndex) {
      this._lastStepIndex = stepIndex;
      this._redraw(this._wordAt(stepIndex));
    }

    // Slam-in: each step starts shifted to the right, decays toward 0.
    const aspect = this.engine.width / this.engine.height;
    const slam = this.v('slam');
    const k = Math.max(0.01, slam);
    const ease = Math.exp(-stepPhase * (4 / k));
    const jitter = (Math.random() - 0.5) * 2 * this.v('jitter') * aspect;
    this.mesh.position.x = slam * aspect * ease + jitter;
  }

  reseed() {
    if (!this._wordList || this._wordList.length < 2) return;
    const arr = this._wordList.slice();
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(this.rand01(i + 7) * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    this._wordList = arr;
    this._lastStepIndex = -1;
    this._redraw(this._wordAt(0));
  }
}
